import React, {
  memo,
  useMemo,
  useState,
  useContext,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useCallback,
} from 'react';
import { Justify } from 'tea-component';
import { useMemoizedFn } from 'ahooks';
import { useHistory, useIntl } from 'umi';
import classnames from 'classnames';
// import { RegionSelect } from 'dbc-region-markets';
import RegionSelector from '@gp/components/RegionSelector';
import GeneralDownloadBtn from '@gp/components/GeneralDownloadBtn';
// @ts-ignore
import { isEqual } from 'lodash-es';
import GPSelectMultiple from '@gp/components/SelectMultiple';
import { context as Context } from '@gp/components/Context';
// @ts-ignore
// import SearchSelect from '@gp/pages/Pipeline/Project/components/SearchSelect';
import SearchSelect from '@gp/components/SearchCommon';
import { FILE_TYPES2, MENU_ID_MAP, SLASH_G_POP } from '@gp/constants/const';
import './index.scss';

const Filter: React.ForwardRefRenderFunction<any, IStudioOverallOverviewKeyInformationFilterProps> = (props, ref) => {
  const { downloadDisabled, downloading, onDownload, formModel: formModelProps, onChange, onSearch, loading } = props;

  const history = useHistory();
  const { locale } = useIntl();

  const { commonData } = useContext<{
    commonData: IStudioOverallOverviewCommonData;
  }>(Context);

  const regionsCountriesData = useMemo(() => {
    const regionsCountriesData = commonData?.regionsCountriesData;
    // 只根据data变化才进行数据改造
    if (!Array.isArray(regionsCountriesData)) return [];
    const newRegionsCountriesData = regionsCountriesData?.map((regionItem) => {
      if (regionItem?.region === 'empty region') {
        // 排除这个empty region
        const { countryData = [] } = regionItem;
        return {}; // 把这个item作为空值返回
      }
      // 根据后端数据格式化成RegionSelect组件数据
      return {
        region_name_en: regionItem?.region,
        region_key: regionItem?.regionAbbre,
        country: regionItem?.countryData.map((el: any) => ({
          country_name_abbre: el?.countryAbbre,
          country_name_en: el?.country,
        })),
      };
    });

    return newRegionsCountriesData;
  }, [commonData?.regionsCountriesData]);

  const options = useMemo(() => {
    const regionsCountriesData = commonData?.regionsCountriesData;
    // 只根据data变化才进行数据改造
    if (!Array.isArray(regionsCountriesData)) return [];
    const newRegionsCountriesData = regionsCountriesData?.map((regionItem) => {
      if (regionItem?.region === 'empty region') {
        // 排除这个empty region
        const { countryData = [] } = regionItem;
        return {}; // 把这个item作为空值返回
      }
      // 根据后端数据格式化成RegionSelect组件数据
      return {
        area_name_en: regionItem?.region,
        area_name_ch: '',
        area_code: regionItem?.regionAbbre,
        country_set: regionItem?.countryData.map((el: any) => ({
          country_code: el?.countryAbbre,
          country_name_en: el?.country,
          country_name_ch: '',
        })),
      };
    });

    return newRegionsCountriesData;
  }, [commonData?.regionsCountriesData]);

  const regionCountriesMaps = useMemo(() => {
    const regionCountry = regionsCountriesData;
    const regionCountriesMap: any = {};
    const countriesNamesMap: any = {};
    let region;
    let regionKey;
    let countries;
    let country;
    let countryKey;
    if (!Array.isArray(regionCountry))
      return {
        regionCountriesMap,
        countriesNamesMap,
      };
    for (let i = 0; i < regionCountry.length; i++) {
      region = regionCountry[i];
      if (!region) continue;
      regionKey = region.region_key?.toLowerCase();
      countries = region.country || [];
      regionCountriesMap[regionKey] = [];
      for (let j = 0; j < countries?.length; j++) {
        country = countries[j];
        if (!country) continue;
        countryKey = country.country_name_abbre?.toLowerCase();
        regionCountriesMap[regionKey].push(country.country_name_en);
        countriesNamesMap[countryKey] = country.country_name_en;
      }
    }
    return {
      regionCountriesMap,
      countriesNamesMap,
    };
  }, [regionsCountriesData]);

  const [formModel, setFormModel] = useState<IStudioOverallOverviewKeyInformationTableFilterFormModel>({
    category: [],
    business: [],
    regionType: 'market',
    regions: ['global'],
  });

  const getRegionsForReq = (regionType: IRegionType, regionValue: IRegions, maps = regionCountriesMaps) => {
    if (!Array.isArray(regionValue)) return [];
    const { regionCountriesMap, countriesNamesMap } = maps || {};
    if (regionType === 'region') {
      return regionValue.reduce((current: any, next: any) => {
        if (typeof next !== 'string') return current;
        const countries = regionCountriesMap?.[next.toLowerCase()] || [];
        return [...current, ...countries];
      }, []);
    }
    if (regionType === 'market') {
      if (regionValue?.includes('global')) return [];
      if (regionValue?.includes('global_wo_china')) return ['global_wo_china'];
      return regionValue
        .filter((item) => typeof item === 'string')
        .map((item) => countriesNamesMap?.[item.toLowerCase()]);
    }

    return [];
  };

  const handleCategoryChange = useMemoizedFn((value: string[]) => {
    const newFormModel = { ...formModel, category: value };
    setFormModel(newFormModel);
    onChange?.(newFormModel);
  });

  const handleBusinessChange = useMemoizedFn((value: string[]) => {
    const newFormModel = { ...formModel, business: value };
    setFormModel(newFormModel);
    onChange?.(newFormModel);
  });

  const handleRegionSelectChange = (payload: any) => {
    const regions = payload?.type === 'region' ? payload?.regions : payload?.markets;
    if (isEqual(payload?.type, formModel?.regionType) && isEqual(regions, formModel?.regions)) return;
    const newFormModel = {
      ...formModel,
      regionType: payload?.type || 'market',
      regions: regions || ['global'],
    };
    setFormModel(newFormModel);
    onChange?.(newFormModel);
  };

  const handleSearchDetailsBtn = useCallback((payload: any, isJump = true) => {
    if (!payload?.studio_id) return '';
    const filter = encodeURIComponent(
      JSON.stringify({
        studioId: payload?.studio_id || '',
        studioName: payload?.studio_name || '',
        menuFrom: MENU_ID_MAP['studio-dashboard_studio-overall'], // 用于标识左侧菜单栏当前选中高亮
      }),
    );
    const url = `${SLASH_G_POP}/Studio/PortfolioOverview/Detail?filter=${filter}`;
    if (isJump) {
      history.push(url);
    }

    return url;
  }, []);

  useImperativeHandle(ref, () => ({
    getRegionsForReq,
  }));

  useEffect(() => {
    if (formModelProps && !isEqual(formModelProps, formModel)) {
      setFormModel({ ...formModelProps });
    }
  }, [formModelProps]);

  return (
    <Justify
      className="gp-studio-overall-key-information-filter font14"
      left={
        <div onClick={(e: any) => e?.stopPropagation()}>
          <GPSelectMultiple
            label="Business Owner"
            disabled={loading}
            searchable={true}
            options={commonData?.businesses || []}
            value={formModel?.business}
            onChange={handleBusinessChange}
          />
          <RegionSelector
            locale={locale}
            defaultValue={formModel?.regions}
            onConfirm={handleRegionSelectChange}
            options={options}
            // value={formModel?.regions}
            // disableNoChina={false}
            globalInfo={null}
            isHiddenGlobalChina
            // isTopCompanies
          />
          {/* <span style={{position: 'relative'}}> */}
          {/* <RegionSelect
              closeAutoPosition={true}
              showChinese={false}
              style={{
                marginTop: 0,
                marginLeft: 0,
                paddingTop: 0,
                marginRight: '10px',
                width: 180,
              }}
              boxStyle={{ left: 0 }}
              className={classnames({
                'gp-common-region-select': true,
                'region-select': true,
                'region-select-disabled': loading,
              })}
              regionType={formModel?.regionType || 'region'}
              value={formModel?.regions || ['global']}
              defaultValue={formModel?.regions || ['global']}
              regionCountry={regionsCountriesData}
              popularList={[]}
              onConfirm={handleRegionSelectChange}
            /> */}
          {/* 代替禁用效果 */}
          {/* {loading && (
              <label
                style={{
                  display: 'block',
                  width: '100%',
                  height: 34,
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  zIndex: 10,
                  cursor: 'no-drop',
                }}
              ></label>
            )} */}
          {/* </span> */}
        </div>
      }
      right={
        <>
          <SearchSelect
            domain="studio"
            currentPage="studioOverview"
            placeholder="Search any Studio in the table"
            submit={(params: any) => {
              onSearch?.({
                ...formModel,
                studioName: params?.studio_name || '',
                studioId: params?.studio_id || '',
              });
            }}
            showRightBtn={true}
            rightBtnText="Details"
            onRightBtnClick={handleSearchDetailsBtn}
          />
          <GeneralDownloadBtn
            style={{ marginLeft: 10, width: 34, height: 34 }}
            uiType="1"
            fileTypes={FILE_TYPES2}
            isDisabled={downloadDisabled}
            isLoading={downloading}
            onConfirm={onDownload}
          />
        </>
      }
    />
  );
};

export default memo(forwardRef(Filter));
